import { trackStatsEvent, useStatsLoaded } from "../utils/statsAnalytics";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { StatsAccountBar, StatsState, StatsError, StatsEmpty, StatsLoading, statsButton } from "./StatsUI";
import StatsComparison, { StatsNote } from "./StatsComparison";
import { useAuth } from "../Context/AuthContext";
import { completeInstagramTest, disconnectInstagramTest, getInstagramTestReels, startInstagramTest } from "../services/instagramTest";

const ASSUMED_LIKE_RATE = 0.06;
const rate = (value) => value == null ? "—" : `${value.toFixed(2)}%`;

function performance(reel) {
  const likes = typeof reel.likes === "number" && reel.likes >= 0 ? reel.likes : null;
  const estimatedViews = likes == null ? null : Math.round(likes / ASSUMED_LIKE_RATE);
  const comments = typeof reel.comments === "number" && reel.comments >= 0 ? reel.comments : null;
  const commentRatePercent = estimatedViews > 0 && comments != null ? (comments / estimatedViews) * 100 : null;
  let tag = "Not enough data";

  if (commentRatePercent != null) {
    if (estimatedViews >= 5000 && commentRatePercent >= 0.15) tag = "On fire";
    else if (estimatedViews >= 1000 && commentRatePercent >= 0.10) tag = "Controversial";
    else if (estimatedViews <= 1000 && commentRatePercent <= 0.02) tag = "Dookie";
    else if (commentRatePercent <= 0.03) tag = "Flop";
    else tag = "Eh";
  }
  return { estimatedViews, commentRatePercent, tag, tagDescription: commentRatePercent == null ? "Likes or comments are missing, or estimated views are zero." : "Heuristic based on estimated views and comment activity; not a sentiment analysis." };
}

const count = (value) => value == null ? "Unavailable" : Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(value);


export default function InstagramTestStats({ access, visible = true }) {
  const { currentUser } = useAuth();
  const [connection, setConnection] = useState(access);
  const [data, setData] = useState(null);
  useStatsLoaded("instagram", data, data?.reels?.length || 0, visible);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const load = useCallback(async ({ after = "", insights = false } = {}) => {
    const id = ++requestId.current;
    setLoadingMore(Boolean(after)); setLoading(true); setError("");
    try {
      const result = await getInstagramTestReels(currentUser, { after, insights });
      if (id === requestId.current) setData((previous) => after ? { ...result, reels: [...new Map([...(previous?.reels || []), ...result.reels].map((reel) => [reel.id, reel])).values()] } : result);
    } catch (failure) { if (id === requestId.current) setError(failure.message); }
    finally { if (id === requestId.current) { setLoading(false); setLoadingMore(false); } }
  }, [currentUser]);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams(window.location.search);
    const handoff = params.get("handoff");
    const callback = params.get("instagram");
    const clean = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("handoff"); url.searchParams.delete("instagram");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    };
    const initialize = async () => {
      let saved = access;
      if (callback === "complete" && handoff) {
        setLoading(true);
        try { saved = await completeInstagramTest(currentUser, handoff); if (active) { setConnection(saved); if (saved.connected) trackStatsEvent("stats_connection_complete", { platform: "instagram" }); } }
        catch (failure) { if (active) setError(failure.message); return; }
        finally { if (active) { setLoading(false); clean(); } }
      } else if (callback) {
        setError(callback === "cancelled" ? "Instagram connection was cancelled." : "Instagram connection could not be completed. Check the app setup and try again.");
        clean(); return;
      }
      if (active && saved.connected && !saved.expired) load({ insights: Boolean(saved.insightsRequested) });
    };
    initialize();
    return () => { active = false; requestId.current += 1; };
  }, [currentUser, access, load]);

  const connect = async () => {
    setError(""); setLoading(true);
    try {
      const result = await startInstagramTest(currentUser);
      const url = new URL(result.authUrl);
      if (url.protocol !== "https:" || url.hostname !== "www.instagram.com" || url.pathname !== "/oauth/authorize") throw new Error("Instagram returned an invalid authorization link.");
      window.location.assign(url.toString());
    } catch (failure) { setError(failure.message); setLoading(false); }
  };
  const disconnect = async () => {
    requestId.current += 1; setLoading(true); setError("");
    try { await disconnectInstagramTest(currentUser); setConnection((value) => ({ ...value, connected: false, profile: null })); setData(null); }
    catch (failure) { setError(failure.message); }
    finally { setLoading(false); }
  };
  return <section aria-label="Instagram">
    {!connection.connected ? <StatsState platform="Instagram" onConnect={connect} loading={loading} error={error} disabled={!connection.configured} title={!connection.configured ? "Instagram connections aren't available right now." : undefined} /> : <>
      <StatsAccountBar handle={connection.profile?.username ? `@${connection.profile.username}` : "Your Instagram account"} picture={connection.profile?.pictureUrl} loading={loading} expired={connection.expired} onRefresh={() => load({ insights: Boolean(connection.insightsRequested) })} onReconnect={connection.expired || error ? connect : undefined} reconnectLabel="Reconnect Instagram" onDisconnect={disconnect} />
      {error && <StatsError onRetry={!connection.expired ? () => load({ insights: Boolean(connection.insightsRequested) }) : undefined} />}
      {connection.expired && <p className="stats-warning">Reconnect to update your stats. Your loaded posts are still here.</p>}
      {loading && !data && <StatsLoading />}
      {!!data?.reels.length && <StatsComparison label="Instagram Reel performance" postLabel="Reel" rows={data.reels.map((reel) => ({ ...reel, ...performance(reel), title: reel.caption || "Untitled Reel", thumbnail: reel.thumbnailUrl, url: reel.permalink, linkLabel: "Watch Reel" }))} columns={[
        { key: "estimatedViews", label: "Est. views", estimated: true, format: (value) => value == null ? "—" : `~${count(value)}` },
        { key: "likes", label: "Likes", format: count }, { key: "comments", label: "Comments", format: count }, { key: "commentRatePercent", label: "Comment rate", format: rate },
      ]} />}
      {!data?.reels.length && !loading && !error && !connection.expired && <StatsEmpty />}
      <StatsNote title="Estimated views, not measured views">Est. views = likes ÷ 0.06, rounded. Comment rate = comments ÷ estimated views × 100. Tags are rule-based labels, not sentiment analysis or verified judgments.</StatsNote>
      {data?.nextCursor && <div className="stats-more"><button type="button" disabled={loading} onClick={() => load({ after: data.nextCursor })} className={statsButton}>{loadingMore ? <Loader2 aria-hidden="true" className="animate-spin" /> : <ChevronDown aria-hidden="true" />}{loadingMore ? "Loading more…" : "Load more posts"}</button></div>}

    </>}
  </section>;
}
